import { FormSection } from "@/components/common/inputs/FormSection";
import { ResourceName, ReimbursementCreate, ReimbursementStatus, ValidAny } from "@/types";
import { getInputProps } from "@/utils/helpers/columnsHelper";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { Box, Stack } from "@mui/material";
import { useEffect } from "react";
import {
  useGetOne,
  ReferenceInput,
  AutocompleteInput,
  TextInput,
  DateInput,
  NumberInput,
  BooleanInput,
  SelectInput,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { parseDateInput, formatDateDisplay } from "@/resources/policies/components/PolicyCreateInputs";

const getReimbursementInputProps = (key: string, textReplace = {}) =>
  getInputProps(key, textReplace, ResourceName.REIMBURSEMENTS);

export const ReimbursementCreateInputs = () => {
  const { setValue } = useFormContext<ReimbursementCreate>();

  // Watched values for conditional UI
  const claimantId = useWatch({ name: "claimant_id_ref" });
  const clientId = useWatch({ name: "client_id_ref" });
  const claimId = useWatch({ name: "claim_id_ref" });
  const status = useWatch({ name: "status" });

  // Auto-fill from selected claimant
  const { data: claimantRecord } = useGetOne(ResourceName.CLAIMANTS, { id: claimantId }, { enabled: !!claimantId });

  // Set default status on mount
  useEffect(() => {
    setValue("status", ReimbursementStatus.PENDING, { shouldDirty: false });
  }, [setValue]);

  useEffect(() => {
    if (claimantRecord) {
      if (claimantRecord.client_id_ref) {
        setValue("client_id_ref", claimantRecord.client_id_ref, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
      if (claimantRecord.partner_id_ref) {
        setValue("partner_id_ref", claimantRecord.partner_id_ref, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    }
  }, [claimantRecord, setValue]);

  // Auto-fill from selected claim
  const { data: claimRecord } = useGetOne(ResourceName.STOPLOSS_CLAIMS, { id: claimId }, { enabled: !!claimId });
  useEffect(() => {
    if (claimRecord?.policy_id_ref) {
      setValue("policy_id_ref", claimRecord.policy_id_ref, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [claimRecord, setValue]);

  const statusChoices = Object.values(ReimbursementStatus).map((s) => ({
    id: s,
    name: s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " "),
  }));

  return (
    <Box p={2}>
      {/* --- References --- */}
      <ReferenceInput source="claimant_id_ref" reference={ResourceName.CLAIMANTS} required perPage={100}>
        <AutocompleteInput
          {...getReimbursementInputProps("claimant_id_ref")}
          isRequired
          size="small"
          filterToQuery={(searchText) => ({ q: searchText })}
          optionText={(record) => `${record.claimant_first_name} ${record.claimant_last_name}`}
        />
      </ReferenceInput>

      <ReferenceInput
        source="claim_id_ref"
        reference={ResourceName.STOPLOSS_CLAIMS}
        filter={{ claimant_id_ref: claimantId }}
        enableGetChoices={({ q }) => !!claimantId || !!q}
        perPage={100}
      >
        <AutocompleteInput
          {...getReimbursementInputProps("claim_id_ref")}
          size="small"
          disabled={!claimantId}
          optionText={(record: ValidAny) => {
            const claimType = record.stoploss_claim_type
              ? `${formatToTitleCase(record.stoploss_claim_type)} Claim`
              : "Claim";
            const status = record.claim_status ? formatToTitleCase(record.claim_status) : undefined;
            const amount =
              record.total_billed_amount !== null && record.total_billed_amount !== undefined
                ? `Billed $${Number(record.total_billed_amount).toLocaleString()}`
                : undefined;
            return [claimType, status, amount].filter(Boolean).join(" · ");
          }}
        />
      </ReferenceInput>

      <ReferenceInput
        source="policy_id_ref"
        reference={ResourceName.POLICIES}
        filter={{ client_id_ref: clientId }}
        enableGetChoices={({ q }) => !!clientId || !!q}
      >
        <AutocompleteInput
          {...getReimbursementInputProps("policy_id_ref")}
          size="small"
          optionText={(record: ValidAny) => {
            const carrier = record.vendor_company_name || record.source_data?.stop_loss_carrier || "Policy";
            const years = formatYearRange(record.effective_start_date, record.effective_end_date);
            return [carrier, years].filter(Boolean).join(" · ");
          }}
          disabled={!clientId}
        />
      </ReferenceInput>

      {/* --- Status and Financial --- */}
      <FormSection title="Status and Financial Details">
        <SelectInput {...getReimbursementInputProps("status")} choices={statusChoices} required size="small" />

        <NumberInput {...getReimbursementInputProps("requested_amount")} required size="small" min={0.01} />

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <NumberInput {...getReimbursementInputProps("reimbursement_amount")} size="small" min={0} />
          <NumberInput {...getReimbursementInputProps("pended_amount")} size="small" min={0} />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <NumberInput {...getReimbursementInputProps("adjustment_amount")} size="small" />
          <NumberInput {...getReimbursementInputProps("denial_amount")} size="small" min={0} />
        </Stack>
      </FormSection>

      {/* --- Reason Fields --- */}
      {(status === ReimbursementStatus.PENDING ||
        status === ReimbursementStatus.ADJUSTED ||
        status === ReimbursementStatus.DENIED ||
        status === ReimbursementStatus.UNDER_REVIEW) && (
        <FormSection title="Reason Details">
          {(status === ReimbursementStatus.PENDING || status === ReimbursementStatus.UNDER_REVIEW) && (
            <TextInput {...getReimbursementInputProps("pending_reason")} multiline rows={3} fullWidth size="small" />
          )}

          {status === ReimbursementStatus.ADJUSTED && (
            <TextInput {...getReimbursementInputProps("adjustment_reason")} multiline rows={3} fullWidth size="small" />
          )}

          {status === ReimbursementStatus.DENIED && (
            <TextInput {...getReimbursementInputProps("denial_reason")} multiline rows={3} fullWidth size="small" />
          )}
        </FormSection>
      )}

      {/* --- Dates --- */}
      <FormSection title="Dates">
        <Stack direction="row" spacing={1}>
          <DateInput
            {...getReimbursementInputProps("received_date")}
            parse={parseDateInput}
            format={formatDateDisplay}
            size="small"
          />
          <DateInput
            {...getReimbursementInputProps("check_date")}
            parse={parseDateInput}
            format={formatDateDisplay}
            size="small"
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <DateInput
            {...getReimbursementInputProps("service_start_date")}
            parse={parseDateInput}
            format={formatDateDisplay}
            size="small"
          />
          <DateInput
            {...getReimbursementInputProps("service_end_date")}
            parse={parseDateInput}
            format={formatDateDisplay}
            size="small"
          />
        </Stack>

        {status === ReimbursementStatus.PENDING && (
          <DateInput
            {...getReimbursementInputProps("pended_audit_date")}
            parse={parseDateInput}
            format={formatDateDisplay}
            size="small"
            sx={{ mt: 2 }}
          />
        )}

        {status === ReimbursementStatus.ADJUSTED && (
          <DateInput
            {...getReimbursementInputProps("adjustment_audit_date")}
            parse={parseDateInput}
            format={formatDateDisplay}
            size="small"
            sx={{ mt: 2 }}
          />
        )}
      </FormSection>

      {/* --- Payment Details --- */}
      <FormSection title="Payment Details">
        <Stack direction="row" spacing={4}>
          <TextInput {...getReimbursementInputProps("check_number")} size="small" />
          <BooleanInput {...getReimbursementInputProps("eft")} label="EFT Payment" size="small" />
        </Stack>

        <TextInput {...getReimbursementInputProps("payee")} fullWidth size="small" sx={{ mt: 2 }} />
      </FormSection>

      {/* --- Administrative Details --- */}
      <FormSection title="Administrative Details">
        <TextInput {...getReimbursementInputProps("claims_administrator")} fullWidth size="small" />

        <TextInput {...getReimbursementInputProps("provider")} fullWidth size="small" />

        <TextInput {...getReimbursementInputProps("auditor_name")} fullWidth size="small" />
      </FormSection>

      {/*
        Omitted on purpose:
        - id, aegle_id (server-generated)
        - expanded fields (claimant, policy, claim, etc. - read-only)
        - client_id_ref, partner_id_ref (auto-filled from claimant)
        - source_documents (handle separately with file upload)
        - created_by/modified_by/deleted* (audit fields)
      */}
    </Box>
  );
};
